import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useQuery } from '@apollo/client';

import { ActionButton, ButtonModel } from '@notino/react-styleguide';

import { withProviders } from '@context/contextConfig';
import { useTabSwitcher } from '@sharedComponents/TabSwitcher/context/TabSwitcherContext';

import { getProductView_productDetailByMasterId_variants } from '../../../queries/__generated__/getProductView';
import {
  getReviewSummary,
  getReviewSummaryVariables,
} from '../../../queries/__generated__/getReviewSummary';
import getReviewSummaryQuery from '../../../queries/reviewSummary.graphql';

import { PAGE_SIZE } from './constants';
import { LoadingWrapper } from './Loading/LoadingWrapper';
import { ReviewsSkeleton } from './Loading/ReviewsSkeleton';
import { ReviewSummarySkeleton } from './Loading/ReviewSummarySkeleton';
import { messages } from './messages';
import {
  getReviews,
  getReviewsVariables,
} from './queries/__generated__/getReviews';
import getReviewsQuery from './queries/reviews.graphql';
import { ReviewsItems } from './ReviewItems';
import {
  ReviewItemsProvider,
  useReviewItemsContext,
} from './ReviewItems/context';
import { SortBox } from './ReviewItems/styled';
import { ReviewsSummary } from './ReviewsSummary';
import { CenterWrapper, ErrorMessage } from './styled';
import { hasQueryMailInLocation } from './utils/hasQueryMailInLocation';

interface IReviewsProps {
  variant: getProductView_productDetailByMasterId_variants;
  productCode: string;
  masterId: string;
  reviewsTabId: number;
  renderReviewsOnServer: boolean;
}

const ReviewsComponent: React.FC<IReviewsProps> = React.memo(
  ({ productCode, masterId, reviewsTabId, renderReviewsOnServer }) => {
    const {
      state: { orderBy, orderDesc, page, hideTranslated },
    } = useReviewItemsContext();

    const queryMailInLocation = hasQueryMailInLocation();

    const { activeTabId, isReviewTabVisibleOnMobile } = useTabSwitcher();
    const isActive =
      activeTabId === reviewsTabId ||
      isReviewTabVisibleOnMobile ||
      queryMailInLocation;

    const {
      data: reviewsSummaryData,
      loading: reviewsSummaryLoading,
      error: reviewsSummaryError,
      refetch: reviewsSummaryRefetch,
    } = useQuery<getReviewSummary, getReviewSummaryVariables>(
      getReviewSummaryQuery,
      {
        skip: renderReviewsOnServer ? false : !isActive,
        ssr: renderReviewsOnServer ? true : isActive,
        variables: {
          masterId,
        },
        notifyOnNetworkStatusChange: true,
      }
    );

    const { data, error, loading, refetch, fetchMore } = useQuery<
      getReviews,
      getReviewsVariables
    >(getReviewsQuery, {
      variables: {
        code: productCode,
        orderBy,
        orderDesc,
        page,
        pageSize: PAGE_SIZE,
        hideTranslated,
      },
      partialRefetch: true,
      skip: renderReviewsOnServer ? false : !isActive,
      ssr: renderReviewsOnServer ? true : isActive,
    });

    if (!isActive && !renderReviewsOnServer) {
      return null;
    }

    const reviewOverview =
      reviewsSummaryData?.productDetailByMasterId?.[0]?.reviewOverview;
    const reviewSummary =
      reviewsSummaryData?.productDetailByMasterId?.[0]?.reviewSummary;

    const textReviewsCount = reviewOverview?.textReviewsCount ?? 0;

    const refetchSummary = async () => {
      await reviewsSummaryRefetch();
    };

    const refetchReviews = async () => {
      await refetch();
    };

    return (
      <div id="pdReviews">
        <LoadingWrapper
          isLoading={reviewsSummaryLoading}
          isError={!reviewSummary || !!reviewsSummaryError}
          skeleton={<ReviewSummarySkeleton />}
          error={
            <CenterWrapper>
              <ErrorMessage>
                <FormattedMessage {...messages.reviewsSummaryFailed} />
              </ErrorMessage>

              <ActionButton
                onClick={refetchSummary}
                buttonSize={ButtonModel.Sizes.xSmall}
              >
                <FormattedMessage {...messages.tryAgain} />
              </ActionButton>
            </CenterWrapper>
          }
        >
          <ReviewsSummary
            reviewSummary={reviewSummary}
            productCode={productCode}
            masterId={masterId}
          />
        </LoadingWrapper>

        <LoadingWrapper
          isLoading={loading}
          isError={!data || !!error}
          skeleton={<ReviewsSkeleton />}
          error={
            <>
              <SortBox />
              <CenterWrapper>
                <ErrorMessage>
                  <FormattedMessage {...messages.reviewsFailed} />
                </ErrorMessage>
                <ActionButton
                  onClick={refetchReviews}
                  buttonSize={ButtonModel.Sizes.xSmall}
                >
                  <FormattedMessage {...messages.tryAgain} />
                </ActionButton>
              </CenterWrapper>
            </>
          }
        >
          <ReviewsItems
            productCode={productCode}
            reviews={data?.reviews || []}
            reviewsLoading={loading}
            reviewsError={error}
            refetch={refetch}
            fetchMore={fetchMore}
            textReviewsCount={textReviewsCount}
          />
        </LoadingWrapper>
      </div>
    );
  }
);

export const Reviews =
  withProviders<IReviewsProps>(ReviewItemsProvider)(ReviewsComponent);
